/**
 * Content Analysis Page - Content Quality Dashboard
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Fade,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { 
  useContentAnalysisSummary, 
  useContentAnalysisPages 
} from '@/hooks/customer';
import { formatCompactNumber, formatPercentage } from '@/services/customer/formatters';
import { DataTable, InlineError, TableSkeleton } from '@/components/customer';
import type { Column } from '@/components/customer';

function CustomerContentAnalysisPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [qualityFilter, setQualityFilter] = useState<string | undefined>();
  
  const { 
    data: summary, 
    isLoading: summaryLoading, 
    error: summaryError, 
    refetch: refetchSummary 
  } = useContentAnalysisSummary();
  
  const {
    data: pagesData,
    isLoading: pagesLoading,
    error: pagesError,
    refetch: refetchPages
  } = useContentAnalysisPages({
    page,
    size: pageSize,
    sortBy: 'analyzed_at',
    sortDirection: 'DESC',
    qualityFilter: qualityFilter as any,
  });

  const isLoading = summaryLoading || pagesLoading;
  const error = summaryError || pagesError;

  if (isLoading && !summary) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4">Content Analysis</Typography>
        </Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={3} key={i}>
              <Card><CardContent><LinearProgress /></CardContent></Card>
            </Grid>
          ))}
        </Grid>
        <TableSkeleton rows={10} columns={6} />
      </Box>
    );
  }

  const getQualityColor = (classification: string) => {
    switch (classification) {
      case 'EXCELLENT': return 'success';
      case 'GOOD': return 'info';
      case 'NEEDS_IMPROVEMENT': return 'warning';
      case 'CRITICAL': return 'error';
      default: return 'default';
    }
  };

  const getSentimentIcon = (score: number) => {
    if (score >= 0.6) return <TrendingUpIcon fontSize="small" color="success" />;
    if (score <= 0.3) return <TrendingDownIcon fontSize="small" color="error" />;
    return null;
  };

  const columns: Column<any>[] = [
    { 
      field: 'url', 
      headerName: 'URL', 
      flex: 2,
      renderCell: (row) => (
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {row.url}
        </Typography>
      ),
    },
    { 
      field: 'wordCount', 
      headerName: 'Word Count', 
      width: 120,
      align: 'right',
      renderCell: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2">{row.wordCount}</Typography>
          {row.flags?.includes('THIN_CONTENT') && <WarningIcon fontSize="small" color="warning" />}
        </Box>
      ),
    },
    { 
      field: 'sentiment', 
      headerName: 'Sentiment', 
      width: 120,
      align: 'right',
      renderCell: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
          <Typography variant="body2">{row.sentiment?.toFixed(2) ?? 'N/A'}</Typography>
          {row.sentiment && getSentimentIcon(row.sentiment)}
        </Box>
      ),
    },
    { 
      field: 'sentimentLabel', 
      headerName: 'Sentiment Label', 
      width: 140,
      renderCell: (row) => (
        <Chip 
          label={row.sentimentLabel || 'N/A'} 
          color={row.sentiment >= 0.6 ? 'success' : row.sentiment <= 0.3 ? 'error' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    { 
      field: 'contentQualityScore', 
      headerName: 'Quality Score', 
      width: 130,
      align: 'right',
      renderCell: (row) => (
        <Typography variant="body2">{row.contentQualityScore?.toFixed(1) ?? 'N/A'}</Typography>
      ),
    },
    { 
      field: 'contentQuality', 
      headerName: 'Classification', 
      width: 150,
      renderCell: (row) => (
        <Chip 
          label={row.contentQuality?.replace('_', ' ') || 'N/A'} 
          color={getQualityColor(row.contentQuality) as any}
          size="small"
        />
      ),
    },
    { 
      field: 'keywordDensity', 
      headerName: 'Keyword Density', 
      width: 140,
      align: 'right',
      renderCell: (row) => (
        <Typography variant="body2">
          {row.keywordDensity ? `${(row.keywordDensity * 100).toFixed(1)}%` : 'N/A'}
        </Typography>
      ),
    },
    { 
      field: 'flags', 
      headerName: 'Flags', 
      width: 200,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {row.flags && row.flags.length > 0 ? (
            row.flags.slice(0, 2).map((flag: string, idx: number) => (
              <Chip 
                key={idx}
                label={flag.replace('_', ' ')}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">None</Typography>
          )}
          {row.flags && row.flags.length > 2 && (
            <Typography variant="caption" color="text.secondary">+{row.flags.length - 2}</Typography>
          )}
        </Box>
      ),
    },
    { 
      field: 'analyzedAt', 
      headerName: 'Analyzed', 
      width: 180,
      renderCell: (row) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(row.analyzedAt).toLocaleString()}
        </Typography>
      ),
    },
  ];

  const pages = pagesData?.pages ?? [];
  const pagination = pagesData?.pagination;

  return (
    <Fade in timeout={300}>
      <Box sx={{ maxWidth: { xs: 400, sm: 768, md: 900, lg: 1200 }, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/early-stage')}
            sx={{ mb: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" gutterBottom>
            Content Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Content quality metrics, sentiment analysis, and recommendations
          </Typography>
        </Box>

        {error && (
          <InlineError 
            error={error} 
            title="Failed to load content analysis" 
            onRetry={() => {
              refetchSummary();
              refetchPages();
            }} 
          />
        )}

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total Pages Analyzed
                </Typography>
                <Typography variant="h3" color="primary">
                  {formatCompactNumber(summary?.totalPages ?? 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Avg Word Count
                </Typography>
                <Typography variant="h3">
                  {Math.round(summary?.avgWordCount ?? 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Avg Sentiment
                </Typography>
                <Typography variant="h3" color={summary && summary.avgSentimentScore >= 0.5 ? 'success.main' : 'warning.main'}>
                  {(summary?.avgSentimentScore ?? 0).toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Avg Quality Score
                </Typography>
                <Typography variant="h3" color="info.main">
                  {(summary?.avgContentQualityScore ?? 0).toFixed(1)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Quality Distribution */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quality Distribution
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CheckCircleIcon fontSize="large" color="success" />
                      <Typography variant="h4">{summary?.qualityDistribution?.excellent ?? 0}</Typography>
                      <Typography variant="caption">Excellent</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CheckCircleIcon fontSize="large" color="info" />
                      <Typography variant="h4">{summary?.qualityDistribution?.good ?? 0}</Typography>
                      <Typography variant="caption">Good</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <WarningIcon fontSize="large" color="warning" />
                      <Typography variant="h4">{summary?.qualityDistribution?.needsImprovement ?? 0}</Typography>
                      <Typography variant="caption">Needs Improvement</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <ErrorIcon fontSize="large" color="error" />
                      <Typography variant="h4">{summary?.qualityDistribution?.critical ?? 0}</Typography>
                      <Typography variant="caption">Critical</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recommendations
                </Typography>
                {summary?.recommendations?.slice(0, 3).map((rec, idx) => (
                  <Alert 
                    key={idx}
                    severity={rec.priority === 'HIGH' ? 'error' : rec.priority === 'MEDIUM' ? 'warning' : 'info'}
                    sx={{ mb: 1, py: 0 }}
                  >
                    <Typography variant="caption">{rec.message}</Typography>
                  </Alert>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Quality Filter Buttons */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <Button 
            variant={!qualityFilter ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setQualityFilter(undefined)}
          >
            All
          </Button>
          <Button 
            variant={qualityFilter === 'EXCELLENT' ? 'contained' : 'outlined'}
            size="small"
            color="success"
            onClick={() => setQualityFilter('EXCELLENT')}
          >
            Excellent
          </Button>
          <Button 
            variant={qualityFilter === 'GOOD' ? 'contained' : 'outlined'}
            size="small"
            color="info"
            onClick={() => setQualityFilter('GOOD')}
          >
            Good
          </Button>
          <Button 
            variant={qualityFilter === 'NEEDS_IMPROVEMENT' ? 'contained' : 'outlined'}
            size="small"
            color="warning"
            onClick={() => setQualityFilter('NEEDS_IMPROVEMENT')}
          >
            Needs Improvement
          </Button>
          <Button 
            variant={qualityFilter === 'CRITICAL' ? 'contained' : 'outlined'}
            size="small"
            color="error"
            onClick={() => setQualityFilter('CRITICAL')}
          >
            Critical
          </Button>
        </Box>

        {/* Pages Table */}
        <Card>
          <DataTable
            columns={columns}
            data={pages}
            loading={pagesLoading}
            pagination={{
              page,
              pageSize,
              totalCount: pagination?.totalElements ?? 0,
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
            }}
          />
        </Card>
      </Box>
    </Fade>
  );
}

export default CustomerContentAnalysisPage;
